import {
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  Package,
  Search,
  ShoppingBag,
  Truck,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import AdminLayout from '../../components/admin/AdminLayout';
import { ResponsiveTable } from '../../components/admin/responsive';
import { api } from '../../services/api';

const formatPhone = (v) => {
  if (!v) return '—';
  const d = v.replace(/\D/g, '');
  if (d.length === 11)
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10)
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return v;
};

const REAIS = (c) =>
  (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function Row({ label, value, copy = false, raw }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(raw ?? value ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[9px] font-black text-slate-400 uppercase tracking-wide shrink-0 w-20">
        {label}
      </span>
      <span className="text-xs text-slate-700 flex-1 truncate">
        {value || '—'}
      </span>
      {copy && value && value !== '—' && (
        <button
          onClick={handleCopy}
          className="shrink-0 text-[9px] text-slate-300 hover:text-[#ff2d6c] transition-colors"
        >
          {copied ? '✓' : <Copy size={10} />}
        </button>
      )}
    </div>
  );
}

const STATUS_CONFIG = {
  PENDING: {
    label: 'Pendente',
    color: 'text-amber-600',
    bg: 'bg-amber-50  border-amber-100',
    Icon: Clock,
  },
  PAID: {
    label: 'Pago',
    color: 'text-green-600',
    bg: 'bg-green-50  border-green-100',
    Icon: CheckCircle2,
  },
  PREPARING: {
    label: 'Preparando',
    color: 'text-blue-600',
    bg: 'bg-blue-50   border-blue-100',
    Icon: Package,
  },
  SHIPPED: {
    label: 'Enviado',
    color: 'text-purple-600',
    bg: 'bg-purple-50 border-purple-100',
    Icon: Truck,
  },
  DELIVERED: {
    label: 'Entregue',
    color: 'text-slate-600',
    bg: 'bg-slate-50  border-slate-100',
    Icon: CheckCircle2,
  },
  CANCELLED: {
    label: 'Cancelado',
    color: 'text-red-600',
    bg: 'bg-red-50    border-red-100',
    Icon: XCircle,
  },
};

const STATUS_FLOW = ['PENDING', 'PAID', 'PREPARING', 'SHIPPED', 'DELIVERED'];

export default function Pedidos() {
  const [pedidos, setPedidos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [busca, setBusca] = useState('');
  const [detalhe, setDetalhe] = useState(null);
  const [atualizando, setAtualizando] = useState(false);
  const [trackingEdit, setTrackingEdit] = useState('');
  const [erro, setErro] = useState('');
  const [resumo, setResumo] = useState(null);

  async function carregar() {
    setCarregando(true);
    try {
      const params = filtroStatus ? `?status=${filtroStatus}` : '';
      const [{ data }, { data: res }] = await Promise.all([
        api.get(`/admin/pedidos${params}`),
        api.get('/admin/pedidos/resumo'),
      ]);
      setPedidos(data.pedidos);
      setResumo(res);
    } catch {
      setErro('Erro ao carregar pedidos.');
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
  }, [filtroStatus]);

  async function atualizarStatus(id, status) {
    setAtualizando(true);
    try {
      const { data } = await api.patch(`/admin/pedidos/${id}/status`, {
        status,
        ...(trackingEdit && { trackingCode: trackingEdit }),
      });
      setPedidos((ps) => ps.map((p) => (p.id === id ? { ...p, ...data } : p)));
      setDetalhe((d) => (d?.id === id ? { ...d, ...data } : d));
    } catch {
      setErro('Erro ao atualizar status.');
    } finally {
      setAtualizando(false);
    }
  }

  const filtrados = pedidos.filter((p) => {
    if (!busca) return true;
    const q = busca.toLowerCase();
    return (
      p.customerName?.toLowerCase().includes(q) ||
      p.customerEmail?.toLowerCase().includes(q) ||
      p.id?.toLowerCase().includes(q)
    );
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-pink-50 rounded-xl flex items-center justify-center">
              <ShoppingBag size={18} className="text-pink-500" />
            </div>
            <div>
              <h1 className="text-lg font-black text-slate-900">Pedidos</h1>
              {resumo && (
                <p className="text-xs text-slate-400">
                  {Object.values(resumo.porStatus || {}).reduce(
                    (a, b) => a + b,
                    0,
                  )}{' '}
                  pedidos · R$ {resumo.totalPagoReais} em vendas
                </p>
              )}
            </div>
          </div>

          {/* Busca */}
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300"
            />
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar nome, email, ID..."
              className="pl-9 pr-4 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-[#ff2d6c] w-56"
            />
          </div>
        </div>

        {/* Filtros */}
        <div className="flex gap-2 flex-wrap">
          {[
            ['', 'Todos'],
            ...Object.entries(STATUS_CONFIG).map(([k, v]) => [k, v.label]),
          ].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFiltroStatus(val)}
              className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                filtroStatus === val
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-50 text-slate-400 border border-slate-100'
              }`}
            >
              {label}
              {resumo?.porStatus?.[val] != null && val !== '' && (
                <span className="ml-1.5 opacity-60">
                  ({resumo.porStatus[val]})
                </span>
              )}
            </button>
          ))}
        </div>

        {erro && (
          <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-xs text-red-600">
            {erro}
          </div>
        )}

        {/* Tabela */}
        {carregando ? (
          <div className="text-center text-slate-400 text-xs py-16 animate-pulse">
            Carregando pedidos...
          </div>
        ) : (
          <ResponsiveTable
            data={filtrados}
            columns={[
              {
                key: 'id',
                label: 'Pedido',
                render: (p) => (
                  <span
                    style={{
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      color: '#94a3b8',
                    }}
                  >
                    #{p.id.slice(0, 8).toUpperCase()}
                  </span>
                ),
              },
              {
                key: 'customerName',
                label: 'Cliente',
                render: (p) => (
                  <div>
                    <p
                      style={{
                        fontSize: '12px',
                        fontWeight: 900,
                        color: '#0f172a',
                      }}
                    >
                      {p.customerName}
                    </p>
                    <p style={{ fontSize: '10px', color: '#94a3b8' }}>
                      {p.customerEmail}
                    </p>
                  </div>
                ),
              },
              {
                key: 'items',
                label: 'Items',
                render: (p) => (
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    {p.items?.length ?? 0} item(s)
                  </span>
                ),
              },
              {
                key: 'totalInCents',
                label: 'Total',
                render: (p) => (
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 900,
                      color: '#0f172a',
                    }}
                  >
                    {REAIS(p.totalInCents)}
                  </span>
                ),
              },
              {
                key: 'createdAt',
                label: 'Data',
                render: (p) => (
                  <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                    {new Date(p.createdAt).toLocaleDateString('pt-BR')}
                  </span>
                ),
              },
              {
                key: 'status',
                label: 'Status',
                render: (p) => {
                  const cfg = STATUS_CONFIG[p.status] ?? STATUS_CONFIG.PENDING;
                  const { Icon } = cfg;
                  return (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '9px',
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        padding: '4px 8px',
                        borderRadius: '9999px',
                        border: '1px solid',
                        ...(() => {
                          const match = cfg.bg.match(/bg-(\w+)-(\d+)/);
                          if (!match) return { borderColor: '#e2e8f0' };
                          const [, color, shade] = match;
                          const shades = {
                            50: '#f8fafc',
                            100: '#f1f5f9',
                            200: '#e2e8f0',
                          };
                          return {
                            backgroundColor: shades[shade] || '#f1f5f9',
                            borderColor: `var(--${color}-100, #e2e8f0)`,
                          };
                        })(),
                      }}
                    >
                      <Icon size={10} style={{ display: 'inline' }} />{' '}
                      {cfg.label}
                    </span>
                  );
                },
              },
              {
                key: 'actions',
                label: 'Ação',
                render: (p) => (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <button
                      onClick={() => {
                        setDetalhe(p);
                        setTrackingEdit(p.trackingCode || '');
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: '#fce7f3',
                        color: '#ff2d6c',
                        border: '1px solid #fbcfe8',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        fontSize: '10px',
                        fontWeight: 900,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor = '#f9c5dd')
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor = '#fce7f3')
                      }
                    >
                      <ExternalLink size={11} /> Ver
                    </button>
                    <select
                      value={p.status}
                      onChange={(e) => atualizarStatus(p.id, e.target.value)}
                      disabled={atualizando}
                      style={{
                        fontSize: '10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '4px 8px',
                        color: '#475569',
                        cursor: 'pointer',
                        opacity: atualizando ? 0.5 : 1,
                      }}
                    >
                      {Object.entries(STATUS_CONFIG).map(([val, { label }]) => (
                        <option key={val} value={val}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                ),
              },
            ]}
            options={{
              emptyMessage: 'Nenhum pedido encontrado.',
            }}
          />
        )}
      </div>

      {/* Painel de detalhe */}
      {detalhe && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          }}
          onClick={() => setDetalhe(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              width: '100%',
              maxWidth: '540px',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '24px',
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <h2
                style={{
                  fontSize: 'clamp(14px, 4vw, 18px)',
                  fontWeight: 900,
                  color: '#0f172a',
                  margin: 0,
                  textTransform: 'uppercase',
                }}
              >
                Pedido #{detalhe.id.slice(0, 8).toUpperCase()}
              </h2>
              <button
                onClick={() => setDetalhe(null)}
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.target.style.background = '#f1f5f9')}
                onMouseLeave={(e) =>
                  (e.target.style.background = 'transparent')
                }
              >
                <X size={16} style={{ color: '#64748b' }} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Status flow */}
              <div className="flex items-center gap-1">
                {STATUS_FLOW.map((s, i) => {
                  const idx = STATUS_FLOW.indexOf(detalhe.status);
                  const done = i <= idx;
                  return (
                    <div key={s} className="flex items-center gap-1 flex-1">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-black shrink-0 ${done ? 'bg-[#ff2d6c] text-white' : 'bg-slate-100 text-slate-400'}`}
                      >
                        {i + 1}
                      </div>
                      {i < STATUS_FLOW.length - 1 && (
                        <div
                          className={`flex-1 h-0.5 ${done && i < idx ? 'bg-[#ff2d6c]' : 'bg-slate-100'}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Cliente */}
              <div className="bg-slate-50 rounded-2xl p-4 space-y-2">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">
                  Cliente
                </p>
                <p className="text-sm font-black text-slate-900">
                  {detalhe.customerName}
                </p>
                <div className="grid grid-cols-1 gap-1">
                  <Row label="Email" value={detalhe.customerEmail} copy />
                  <Row label="CPF" value={detalhe.customerCpf} />
                  <Row
                    label="WhatsApp"
                    value={formatPhone(detalhe.customerPhone)}
                    copy
                    raw={detalhe.customerPhone}
                  />
                </div>
              </div>

              {/* Endereço */}
              <div className="bg-slate-50 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    Endereço de entrega
                  </p>
                  <button
                    onClick={() => {
                      const addr = [
                        `${detalhe.addressStreet}, ${detalhe.addressNumber}${detalhe.addressComplement ? ` ${detalhe.addressComplement}` : ''}`,
                        detalhe.addressNeighborhood,
                        `${detalhe.addressCity} - ${detalhe.addressState}`,
                        `CEP ${detalhe.addressCep}`,
                      ].join(', ');
                      navigator.clipboard.writeText(addr);
                    }}
                    className="flex items-center gap-1 text-[9px] text-slate-400 hover:text-[#ff2d6c] font-black uppercase tracking-wide transition-colors"
                  >
                    <Copy size={10} /> Copiar
                  </button>
                </div>
                <div className="space-y-1">
                  <Row
                    label="Logradouro"
                    value={`${detalhe.addressStreet}, ${detalhe.addressNumber}${detalhe.addressComplement ? ` — ${detalhe.addressComplement}` : ''}`}
                  />
                  <Row label="Bairro" value={detalhe.addressNeighborhood} />
                  <Row
                    label="Cidade/UF"
                    value={`${detalhe.addressCity} / ${detalhe.addressState}`}
                  />
                  <Row
                    label="CEP"
                    value={detalhe.addressCep?.replace(
                      /(\d{5})(\d{3})/,
                      '$1-$2',
                    )}
                    copy
                  />
                </div>
              </div>

              {/* Itens */}
              <div className="space-y-2">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                  Itens
                </p>
                {detalhe.items?.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-10 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                        {item.product?.coverBase64 && (
                          <img
                            src={item.product.coverBase64}
                            alt={item.productName}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div>
                        <p className="text-xs font-black text-slate-900">
                          {item.productName}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          × {item.quantity} · {REAIS(item.priceInCents)} cada
                        </p>
                      </div>
                    </div>
                    <span className="text-xs font-black text-slate-900">
                      {REAIS(item.priceInCents * item.quantity)}
                    </span>
                  </div>
                ))}
                <div className="border-t border-slate-100 pt-2 flex justify-between">
                  <span className="text-xs font-black text-slate-500 uppercase">
                    Total
                  </span>
                  <span className="text-sm font-black text-[#ff2d6c]">
                    {REAIS(detalhe.totalInCents)}
                  </span>
                </div>
              </div>

              {/* Rastreamento */}
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '10px',
                    fontWeight: 900,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: '8px',
                  }}
                >
                  Código de rastreamento
                </label>
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    flexDirection: window.innerWidth < 480 ? 'column' : 'row',
                  }}
                >
                  <input
                    type="text"
                    value={trackingEdit}
                    onChange={(e) => setTrackingEdit(e.target.value)}
                    placeholder="BR123456789BR"
                    style={{
                      flex: 1,
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#ff2d6c')}
                    onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                  />
                  <button
                    onClick={() => atualizarStatus(detalhe.id, detalhe.status)}
                    disabled={atualizando}
                    style={{
                      background: '#0f172a',
                      color: '#fff',
                      padding: '12px 24px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      fontWeight: 900,
                      border: 'none',
                      cursor: atualizando ? 'not-allowed' : 'pointer',
                      opacity: atualizando ? 0.5 : 1,
                      transition: 'background-color 0.2s',
                      minHeight: '44px',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={(e) =>
                      !atualizando && (e.target.style.background = '#1e293b')
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.background = '#0f172a')
                    }
                  >
                    {atualizando ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>

              {/* Mudar status */}
              <div>
                <p
                  style={{
                    fontSize: '10px',
                    fontWeight: 900,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: '12px',
                    margin: 0,
                  }}
                >
                  Avançar status
                </p>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      window.innerWidth < 480
                        ? '1fr'
                        : 'repeat(auto-fit, minmax(100px, 1fr))',
                    gap: '8px',
                  }}
                >
                  {Object.entries(STATUS_CONFIG).map(([val, { label }]) => (
                    <button
                      key={val}
                      onClick={() => atualizarStatus(detalhe.id, val)}
                      disabled={atualizando || detalhe.status === val}
                      style={{
                        fontSize: '12px',
                        padding: '10px 16px',
                        borderRadius: '10px',
                        border: '1px solid',
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        cursor:
                          atualizando || detalhe.status === val
                            ? 'not-allowed'
                            : 'pointer',
                        opacity:
                          atualizando || detalhe.status === val ? 0.3 : 1,
                        transition: 'all 0.2s',
                        minHeight: '44px',
                        ...(detalhe.status === val
                          ? {
                              background: '#0f172a',
                              color: '#fff',
                              borderColor: '#0f172a',
                            }
                          : {
                              background: '#fff',
                              color: '#64748b',
                              borderColor: '#e2e8f0',
                            }),
                      }}
                      onMouseEnter={(e) => {
                        if (!atualizando && detalhe.status !== val) {
                          e.target.style.borderColor = '#94a3b8';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!atualizando && detalhe.status !== val) {
                          e.target.style.borderColor = '#e2e8f0';
                        }
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
